/**
 * Session store for persisting workspace state across app restarts.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

// Only suppress logs and use mock storage in unit tests (vitest), not E2E tests
// Unit tests run in Node.js (no window), E2E tests run in Electron (have window.electronAPI)
function isUnitTest(): boolean {
  if (typeof process === 'undefined' || process.env.NODE_ENV !== 'test') {
    return false;
  }
  // In Node.js (vitest), window is undefined
  if (typeof window === 'undefined') {
    return true;
  }
  // In Electron (E2E), window exists and has electronAPI
  return !window.electronAPI;
}
import type {
  Session,
  SerializedTab,
  SessionConnection,
  SerializedNavigationTree,
  SessionRestoreOptions,
} from '../types/sessionTypes';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useTabHistoryStore } from '../../tab-history/store/tabHistoryStore';
import type { Tab } from '../../workspace/core/types/workspaceTypes';
import {
  isExecutionPlanTab,
  isQueryTab,
  isSecurityManagerTab,
  isTableDesignerTab,
  isTableEditorTab,
  isTableExplorerTab,
  isSchemaCompareTab,
  isDataCompareTab,
  isDependencyViewerTab,
  isSchemaOverviewTab,
  isSpatialExplorerTab,
  isDataProfileTab,
  isStartPageTab,
  isNotebookTab,
  isSessionBrowserTab,
  isTablespaceManagerTab,
  isOracleObjectDetailTab,
  isDataPumpTab,
  isInstanceMonitorTab,
  isFilegroupManagerTab,
  isPlSqlDebuggerTab,
  isPlSqlEditorTab,
  isJobSchedulerTab,
  isPackageExplorerTab,
  isDashboardTab,
  isReferencesTab,
  isDatabaseBlueprintTab,
  isCrossEngineMigrateTab,
  isDataImportTab,
  isDeadlockAnalyzerTab,
  isQueryStoreTab,
  isStorageExplorerTab,
} from '../../workspace/core/types/workspaceTypes';
import type { Connection } from '../../../types/electron';
import { serializeForSession } from '../../workspace/table-explorer/components/FkBreadcrumb/breadcrumbTree';
import { serializeEntityViewForSession } from '../../workspace/storage-explorer/entities/model/entityViewPersistence';
import { deserializeTab } from '../services/tabDeserializer';
import { safeTabConnectionInfo, safeTabTitle } from '../../workspace/core/store/safeTabText';
import { safeSession } from '../utils/safeSessionText';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';
import { keepRecords, mapIfArray, rewritePersistedState } from '../../../core/storage/rewritePersistedState';
import { showPersistWriteFailureToast } from '../../../components/ui/persistWriteFailureToast';
import {
  capSessionForPersistence,
  capSessionsForPersistence,
  isUntouchedOmittedDesignerDraft,
  isUntouchedPendingReload,
} from '../services/sessionPersistenceCap';
import { formatShortDate, getConnectedConnectionIds } from '../utils/sessionDisplayUtils';
import {
  flushPendingSessionAutoSaveBeforeRestore,
  pauseAutoSaveForSessionSwitch,
  releaseSessionSwitchPause,
} from '../services/sessionAutoSave';
import {
  clearLazyConnectState,
  forgetLazyConnect,
  isEntraInteractiveConnection,
  markConnectionsLazy,
  resolveEagerConnectionIds,
  sweepPendingLazyConnects,
} from '../services/lazyConnect';
import { connectionNeedsPasswordPrompt } from '../../connections/utils/connectPasswordPredicate';
import { tabConnectionIds } from '../utils/tabConnectionIds';

// ============================================================================
// Serialization Helpers
// ============================================================================

/**
 * Serialize a tab for persistence.
 * Only keeps essential data that can be restored.
 */
function serializeTab(tab: Tab): SerializedTab {
  const base = {
    id: tab.id,
    type: tab.type,
    // Never raw secret-bearing target text in a session file (security slice) — the store already holds safe text;
    // this is the belt to its braces for anything that reached a tab another way.
    title: safeTabTitle(tab.title),
    customTitle: tab.customTitle,
    connectionInfo: safeTabConnectionInfo(tab.connectionInfo),
    ...(tab.sourcePin ? { sourcePin: tab.sourcePin } : {}),
  };

  if (isQueryTab(tab)) {
    // A tab still waiting to reload its omitted script (pendingFileReload)
    // holds only a placeholder; snapshot it as the omitted file-tied tab it
    // is, so a quit or failed read never turns the binding into comment text.
    // Only while the placeholder is untouched and the tab is still detached —
    // text the user typed meanwhile, or another file opened here, wins.
    // chartConfigs intentionally NOT persisted — they reference result columns that
    // don't exist after session restore (results are runtime-only).
    // Persist transaction mode and isolation level preferences (state resets on restore)
    const preferences = {
      ...(tab.content.transactionMode !== 'auto' ? { transactionMode: tab.content.transactionMode } : {}),
      ...(tab.content.isolationLevel !== 'default' ? { isolationLevel: tab.content.isolationLevel } : {}),
      ...(tab.content.aiChatSessionId ? { aiChatSessionId: tab.content.aiChatSessionId } : {}),
      ...(tab.content.aiChatSidebarOpen ? { aiChatSidebarOpen: true } : {}),
      // The Visual view is a preference of the tab, like its transaction mode.
      ...(tab.content.viewMode === 'visual'
        ? {
            viewMode: 'visual' as const,
            ...(typeof tab.content.visualStatementOffset === 'number'
              ? { visualStatementOffset: tab.content.visualStatementOffset }
              : {}),
          }
        : {}),
    };
    if (isUntouchedPendingReload(tab.content)) {
      return {
        ...base,
        content: { query: '', queryOmitted: true, filePath: tab.content.pendingFileReload, ...preferences },
      };
    }
    return {
      ...base,
      content: {
        query: tab.content.query,
        ...(tab.content.filePath ? { filePath: tab.content.filePath } : {}),
        ...(tab.content.fileLastSavedHash ? { fileLastSavedHash: tab.content.fileLastSavedHash } : {}),
        ...preferences,
      },
    };
  }

  if (isTableEditorTab(tab)) {
    return {
      ...base,
      content: {
        tableName: tab.content.tableName,
        schema: tab.content.schema,
      },
    };
  }

  if (isTableExplorerTab(tab)) {
    const navTree = tab.content.navigationTree;
    return {
      ...base,
      content: {
        tableName: tab.content.tableName,
        schema: tab.content.schema,
        database: tab.content.database,
        limit: tab.content.limit,
        selectedColumns: tab.content.selectedColumns,
        filters: tab.content.filters,
        sorting: tab.content.sorting,
        // Slice 6 (FK Breadcrumb) — persist the navigation tree so the
        // chip-row + active view survive a session round-trip. Only emit
        // when present + non-trivial.
        ...(navTree
          ? { navigationTree: serializeForSession(navTree) as unknown as SerializedNavigationTree }
          : {}),
      },
    };
  }

  if (isExecutionPlanTab(tab)) {
    return {
      ...base,
      content: {
        view: tab.content.view ?? 'single',
        filePath: tab.content.filePath,
        baselineFilePath: tab.content.baselineFilePath,
        compareFilePath: tab.content.compareFilePath,
      },
    };
  }

  if (isTableDesignerTab(tab)) {
    return {
      ...base,
      content: {
        mode: tab.content.mode,
        tableName: tab.content.tableName,
        schema: tab.content.schema,
        database: tab.content.database,
        columns: tab.content.columns ?? [],
        ...(tab.content.originalState ? { originalState: tab.content.originalState } : {}),
        ...(tab.content.initialSql !== undefined ? { initialSql: tab.content.initialSql } : {}),
        ...(tab.content.currentSql !== undefined ? { currentSql: tab.content.currentSql } : {}),
        ...(tab.content.columnRenames?.length ? { columnRenames: tab.content.columnRenames } : {}),
        ...(tab.content.viewSqlMode ? { viewSqlMode: tab.content.viewSqlMode } : {}),
        ...(tab.content.isDirty ? { isDirty: tab.content.isDirty } : {}),
        // Carry the omission across re-serialization while the tab is still in
        // the state the cap left it, the same rule the omitted query/VQE tabs
        // follow: without it the first autosave would persist a clean-looking
        // designer and the next restart would show no banner, no Save reason
        // and no hint that a draft was ever discarded.
        ...(isUntouchedOmittedDesignerDraft(tab.content) ? { sqlOmitted: true } : {}),
      },
    };
  }

  if (isSecurityManagerTab(tab)) {
    return {
      ...base,
      content: {
        view: tab.content.view,
        objectType: tab.content.objectType,
        ...(tab.content.selectedDatabase ? { selectedDatabase: tab.content.selectedDatabase } : {}),
      },
    };
  }

  if (isSchemaCompareTab(tab)) {
    return {
      ...base,
      content: {
        source: tab.content.source,
        target: tab.content.target,
        options: tab.content.options,
        // F07: persist explicitly (a plain truthy check would drop `false`,
        // which is exactly the silent-reset-to-true bug this closes).
        ...(tab.content.compareWholeDatabase !== undefined
          ? { compareWholeDatabase: tab.content.compareWholeDatabase }
          : {}),
        // W3.4/F07 fix round 4: the blueprint scope-cap marker. Same explicit
        // `!== undefined` test and for a sharper reason — an empty array means
        // "no caps survive", and dropping it would let `pickInheritedCaps` fall
        // back to the endpoint-kind heuristic on the next Change. Until this
        // line existed the marker died on every auto-save, so a restart plus two
        // ordinary Changes wrote a blueprint's narrowed scope in as the engine
        // default.
        ...(tab.content.scopeCapKeys !== undefined
          ? { scopeCapKeys: tab.content.scopeCapKeys }
          : {}),
        // #98: "Wrap in a single transaction". Same explicit `!== undefined`
        // test as `compareWholeDatabase`, and now for a sharper reason — the
        // default is ON for SQL Server, so `false` is a user who unticked the
        // box, and dropping it re-imposed the wrap on the next launch. Until
        // this line existed the field died on every auto-save, which made the
        // "persisted per tab" claim in the tab's own JSDoc untrue.
        ...(tab.content.wrapScriptInTransaction !== undefined
          ? { wrapScriptInTransaction: tab.content.wrapScriptInTransaction }
          : {}),
      },
    };
  }

  if (isDataCompareTab(tab)) {
    return {
      ...base,
      content: {
        source: tab.content.source,
        target: tab.content.target,
        keyMappings: tab.content.keyMappings,
        valueMappings: tab.content.valueMappings,
        options: tab.content.options,
        // F07: results filters + sorting, previously dropped by the serializer.
        ...(tab.content.filters ? { filters: tab.content.filters } : {}),
        ...(tab.content.sorting ? { sorting: tab.content.sorting } : {}),
      },
    };
  }

  if (isDependencyViewerTab(tab)) {
    return {
      ...base,
      content: {
        objectName: tab.content.objectName,
        objectType: tab.content.objectType,
        schema: tab.content.schema,
        database: tab.content.database,
        viewMode: tab.content.viewMode,
        ...(tab.content.filterState ? { filterState: tab.content.filterState } : {}),
        // B7: persist only the search term; the outcome re-runs on restore.
        ...(tab.content.findUsages?.searchTerm
          ? { findUsagesTerm: tab.content.findUsages.searchTerm }
          : {}),
      },
    };
  }

  if (isSchemaOverviewTab(tab)) {
    return {
      ...base,
      content: {
        connectionId: tab.content.connectionId,
        databaseName: tab.content.databaseName,
        ...(tab.content.filterMode ? { filterMode: tab.content.filterMode } : {}),
      },
    };
  }

  if (isDataProfileTab(tab)) {
    return {
      ...base,
      content: {
        connectionId: tab.content.connectionId,
        database: tab.content.database,
        schema: tab.content.schema,
        table: tab.content.table,
        dataMode: tab.content.dataMode,
        sampleSize: tab.content.sampleSize,
        bucketCount: tab.content.bucketCount,
        histogramMode: tab.content.histogramMode,
        topN: tab.content.topN,
        ...(tab.content.focusedColumn ? { focusedColumn: tab.content.focusedColumn } : {}),
        ...(tab.content.filters && tab.content.filters.length > 0 ? { filters: tab.content.filters } : {}),
        ...(tab.content.rowLimit !== undefined ? { rowLimit: tab.content.rowLimit } : {}),
        ...(tab.content.hideLargeTableHint ? { hideLargeTableHint: true } : {}),
      },
    };
  }

  if (isStartPageTab(tab)) {
    return {
      ...base,
      content: {
        ...(tab.content.expandedSections ? { expandedSections: tab.content.expandedSections } : {}),
        ...(tab.content.tabSearchQuery ? { tabSearchQuery: tab.content.tabSearchQuery } : {}),
        ...(tab.content.selectedConnectionId !== undefined
          ? { selectedConnectionId: tab.content.selectedConnectionId }
          : {}),
      },
    };
  }

  if (isNotebookTab(tab)) {
    // Persist cell sources and order, but strip outputs and execution state
    const persistedCells = tab.content.cells.map((cell) => ({
      id: cell.id,
      cellType: cell.cellType,
      source: cell.source,
      ...(cell.cellType === 'markdown' ? { isEditing: false } : {}),
      ...((cell.cellType === 'code' || cell.cellType === 'javascript') && 'chartConfig' in cell && cell.chartConfig
        ? { chartConfig: cell.chartConfig } : {}),
    }));
    return {
      ...base,
      content: {
        cells: persistedCells,
        metadata: tab.content.metadata,
        executionCounter: 0,
        isDirty: false,
        ...(tab.content.filePath ? { filePath: tab.content.filePath } : {}),
        ...(tab.content.lastSavedHash ? { lastSavedHash: tab.content.lastSavedHash } : {}),
        ...(tab.content.aiChatSessionId ? { aiChatSessionId: tab.content.aiChatSessionId } : {}),
        ...(tab.content.aiChatSidebarOpen ? { aiChatSidebarOpen: true } : {}),
      },
    };
  }

  if (isSessionBrowserTab(tab)) {
    return { ...base, content: {} };
  }

  if (isTablespaceManagerTab(tab)) {
    return { ...base, content: {} };
  }

  if (isOracleObjectDetailTab(tab)) {
    return {
      ...base,
      content: {
        objectName: tab.content.objectName,
        schema: tab.content.schema,
        database: tab.content.database,
        objectKind: tab.content.objectKind,
      },
    };
  }

  if (isDataPumpTab(tab)) {
    return { ...base, content: {} };
  }

  if (isInstanceMonitorTab(tab)) {
    return { ...base, content: {} };
  }

  if (isFilegroupManagerTab(tab)) {
    return { ...base, content: {} };
  }

  if (isPlSqlDebuggerTab(tab)) {
    return {
      ...base,
      content: {
        objectName: tab.content.objectName,
        schema: tab.content.schema,
        database: tab.content.database,
        objectType: tab.content.objectType,
        // Anonymous-block source lives only on the tab (not in ALL_SOURCE),
        // so persist it. Named objects re-fetch from the DB on restore.
        ...(tab.content.objectType === 'anonymous' && tab.content.source
          ? { source: tab.content.source }
          : {}),
      },
    };
  }

  if (isPlSqlEditorTab(tab)) {
    const stripBreakpointRuntime = (bp: { line: number; enabled: boolean; condition?: string; hitCount?: number; hitCountMode?: string }) => ({
      line: bp.line,
      enabled: bp.enabled,
      ...(bp.condition ? { condition: bp.condition } : {}),
      ...(bp.hitCount ? { hitCount: bp.hitCount } : {}),
      ...(bp.hitCountMode ? { hitCountMode: bp.hitCountMode } : {}),
    });
    const bpByProgram: Record<string, Array<{ line: number; enabled: boolean; condition?: string; hitCount?: number; hitCountMode?: string }>> = {};
    for (const [key, bps] of Object.entries(tab.content.breakpointsByProgram)) {
      bpByProgram[key] = bps.map(stripBreakpointRuntime);
    }
    // During step-into, content.breakpoints reflects the foreign program.
    // Always serialize the original program's breakpoints for correct restore.
    const origNs = tab.content.objectType === 'package' ? 2
      : tab.content.objectType === 'trigger' ? 3
      : tab.content.objectType === 'anonymous' ? 0 : 1;
    const origKey = `${tab.content.schema.toUpperCase()}.${tab.content.objectName.toUpperCase()}.${origNs}`;
    const origBreakpoints = bpByProgram[origKey] ?? [];
    return {
      ...base,
      content: {
        objectName: tab.content.objectName,
        schema: tab.content.schema,
        database: tab.content.database,
        objectType: tab.content.objectType,
        sourceView: tab.content.sourceView,
        compiledWithDebug: tab.content.compiledWithDebug,
        memberNavOpen: tab.content.memberNavOpen,
        breakpoints: origBreakpoints,
        breakpointsByProgram: bpByProgram,
        watchExpressions: tab.content.watchExpressions.map(w => ({ id: w.id, expression: w.expression })),
        exceptionBreakpoints: tab.content.exceptionBreakpoints,
      },
    };
  }

  if (isJobSchedulerTab(tab)) {
    return { ...base, content: {} };
  }

  if (isPackageExplorerTab(tab)) {
    return {
      ...base,
      content: {
        connectionId: tab.content.connectionId,
        database: tab.content.database,
        ...(tab.content.searchQuery ? { searchQuery: tab.content.searchQuery } : {}),
        ...(tab.content.leftPanelWidthPx !== undefined ? { leftPanelWidthPx: tab.content.leftPanelWidthPx } : {}),
      },
    };
  }

  if (isDashboardTab(tab)) {
    // Only the repository pointer persists — the definition lives in
    // {userData}/dashboards and tile results are runtime-only.
    return {
      ...base,
      content: {
        dashboardId: tab.content.dashboardId ?? null,
      },
    };
  }

  if (isReferencesTab(tab)) {
    return {
      ...base,
      content: {
        connectionId: tab.content.connectionId,
        database: tab.content.database,
        engineId: tab.content.engineId,
        identifier: tab.content.identifier,
        identifierKind: tab.content.identifierKind,
        ...(tab.content.relation ? { relation: tab.content.relation } : {}),
      },
    };
  }

  if (isDatabaseBlueprintTab(tab)) {
    return {
      ...base,
      content: {
        blueprintFolderId: tab.content.blueprintFolderId,
        ...(tab.content.selectedFilePath ? { selectedFilePath: tab.content.selectedFilePath } : {}),
        ...(tab.content.selectedLinkedDbLabel ? { selectedLinkedDbLabel: tab.content.selectedLinkedDbLabel } : {}),
        ...(tab.content.activePanel ? { activePanel: tab.content.activePanel } : {}),
      },
    };
  }

  if (isCrossEngineMigrateTab(tab)) {
    // Persist the migration CONFIG only; the in-flight run is never restored.
    return {
      ...base,
      content: {
        scope: tab.content.scope,
        source: tab.content.source,
        ...(tab.content.target ? { target: tab.content.target } : {}),
        ...(tab.content.subset !== undefined ? { subset: tab.content.subset } : {}),
      },
    };
  }

  if (isSpatialExplorerTab(tab)) {
    // Layer DEFINITIONS only (T17 whitelist) — feature data is NEVER persisted; each layer
    // reloads from its table/query source when the restored (dormant) tab is activated.
    return {
      ...base,
      content: {
        connectionId: tab.content.connectionId,
        databaseName: tab.content.databaseName,
        view: tab.content.view,
      },
    };
  }

  if (isDataImportTab(tab)) {
    // Persist the wizard CONFIG only — the live analysis and any run/progress
    // state live in the per-tab `dataImportStore` and are never restored.
    return {
      ...base,
      content: {
        step: tab.content.step,
        ...(tab.content.filePath ? { filePath: tab.content.filePath } : {}),
        ...(tab.content.parse ? { parse: tab.content.parse } : {}),
        ...(tab.content.target ? { target: tab.content.target } : {}),
        ...(tab.content.columnOverrides && tab.content.columnOverrides.length > 0
          ? { columnOverrides: tab.content.columnOverrides }
          : {}),
        ...(tab.content.mappings && tab.content.mappings.length > 0 ? { mappings: tab.content.mappings } : {}),
        ...(tab.content.rowFilter ? { rowFilter: tab.content.rowFilter } : {}),
      },
    };
  }

  if (isQueryStoreTab(tab)) {
    // Identity + preferences only — rows, query text and plans are TanStack Query cache, never session state.
    const c = tab.content;
    return {
      ...base,
      content: {
        database: c.database,
        periodHours: c.periodHours,
        ...(c.anchorUtc ? { anchorUtc: c.anchorUtc } : {}),
        includeActiveInterval: c.includeActiveInterval,
        metric: c.metric,
        minExecutions: c.minExecutions,
        limit: c.limit,
        ...(c.sort ? { sort: { id: c.sort.id, desc: c.sort.desc } } : {}),
        ...(c.selectedQueryId ? { selectedQueryId: c.selectedQueryId } : {}),
      },
    };
  }

  if (isDeadlockAnalyzerTab(tab)) {
    // Source metadata only — the XML/parsed report stays in memory (`deadlockAnalyzerStore`).
    return {
      ...base,
      content: {
        sourceKind: tab.content.sourceKind,
        ...(tab.content.filePath ? { filePath: tab.content.filePath } : {}),
        ...(tab.content.fileName ? { fileName: tab.content.fileName } : {}),
        ...(typeof tab.content.selectedIncidentIndex === 'number'
          ? { selectedIncidentIndex: tab.content.selectedIncidentIndex }
          : {}),
        ...(tab.content.selectedNodeId ? { selectedNodeId: tab.content.selectedNodeId } : {}),
        ...(tab.content.connectionId ? { connectionId: tab.content.connectionId } : {}),
        ...(tab.content.collectorRange ? { collectorRange: tab.content.collectorRange } : {}),
        ...(tab.content.collectorDatabase ? { collectorDatabase: tab.content.collectorDatabase } : {}),
      },
    };
  }

  if (isStorageExplorerTab(tab)) {
    // Azure Storage (spec §6.1): an EXPLICIT whitelist, never a spread of the
    // live content — the runtime `openToken` / any listing state a caller
    // stashed on the content must not reach disk. Placed BEFORE the
    // unknown-type fallback below (which would answer `{ query: '' }`).
    const c = tab.content;
    return {
      ...base,
      content: {
        connectionId: c.connectionId,
        root: c.root,
        resource: c.resource,
        mode: c.mode,
        view: {
          listing: c.view.listing,
          prefixFilter: c.view.prefixFilter,
          showSnapshots: c.view.showSnapshots,
          showVersions: c.view.showVersions,
          showDeleted: c.view.showDeleted,
        },
        // Same whitelist, JSON-safe: Int64 chips are native bigints in memory,
        // which JSON.stringify cannot write — one such chip used to drop the
        // WHOLE session write (every SQL tab included). S4 review B, R1.
        ...(c.entityView ? { entityView: serializeEntityViewForSession(c.entityView) } : {}),
        ...(c.messageView ? { messageView: { peekCount: c.messageView.peekCount, decodeBase64: c.messageView.decodeBase64 } } : {}),
      },
    };
  }

  // Fallback for unknown types - log warning
  // Cast to unknown first to handle exhaustive type narrowing
  const unknownTab = tab as unknown as { type: string };
  if (!isUnitTest()) {
    console.warn(`[SessionStore] Unknown tab type during serialization: ${unknownTab.type}`);
  }
  return {
    ...base,
    content: { query: '' },
  };
}

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * A session is "empty" when it has no active connections AND no meaningful tabs
 * (i.e. only the default Home/start-page tab, or nothing at all). Empty sessions
 * are kept as `currentSession` so start-page customizations persist across launches,
 * but they are excluded from `savedSessions` so the Sessions dialog isn't cluttered
 * with "Untitled Session" stubs auto-created from the default Home tab.
 */
function isEmptySession(session: Pick<Session, 'tabs' | 'connections'>): boolean {
  if (session.connections.length > 0) return false;
  return !session.tabs.some((tab) => tab.type !== 'start-page');
}

/** `isEmptySession` for a record read from storage: a damaged one is kept, never evaluated into a throw. */
function isEmptyStoredSession(session: unknown): boolean {
  const candidate = session as Partial<Pick<Session, 'tabs' | 'connections'>> | null;
  if (!candidate || !Array.isArray(candidate.connections) || !Array.isArray(candidate.tabs)) return false;
  if (candidate.tabs.some((tab) => !tab || typeof tab !== 'object')) return false;
  return isEmptySession(candidate as Pick<Session, 'tabs' | 'connections'>);
}

/** The stored session document with only its secret-bearing fields made safe (the same object when none changed). */
/** The current session made safe; one that is not a record (a damaged file) is not kept (review round 5, N5). */
function readableSession<T>(session: T): T | null {
  if (!session) return session;
  return typeof session === 'object' && !Array.isArray(session) ? safeSession(session as unknown as Session) as unknown as T : null;
}

function safeStoredSessions(stored: Record<string, unknown>): Record<string, unknown> {
  const savedSessions = mapIfArray(keepRecords(stored.savedSessions), safeSession);
  const currentSession = readableSession(stored.currentSession);
  return savedSessions === stored.savedSessions && currentSession === stored.currentSession ? stored : { ...stored, savedSessions, currentSession };
}

/**
 * Generate a default session name based on active connections and tabs.
 *
 * Returns `null` when nothing derivable exists (no connection names in either
 * tabs or active connections) — the caller decides what to do with that: fall
 * back to a dated placeholder for a genuinely new/empty session, or keep
 * whatever name the session already had rather than downgrading it.
 */
function generateSessionName(
  tabs: Tab[],
  connections: SessionConnection[],
  resolveConnectionName: (connectionId: string) => string | undefined
): string | null {
  const connectionNames = new Set<string>();

  // The session's DECLARED connections are the authority, not the live ones.
  // Naming from `activeConnections` meant liveness leaked into the name: a
  // session with two connections whose second one happened to be closed at
  // save time was named after the first alone, and it stayed that way — the
  // name looked frozen for a reason the user could not see. Declared
  // membership changes only when a connection is added or removed, which is
  // exactly when the name should follow.
  connections.forEach(({ connectionId }) => {
    const name = resolveConnectionName(connectionId);
    if (name) {
      connectionNames.add(name);
    }
  });

  // Tabs are the fallback, not a second source: a tab carries a SNAPSHOT of
  // the alias it was opened with, so mixing the two would name a renamed
  // connection twice ("New name & Old name").
  if (connectionNames.size === 0) {
    tabs.forEach((tab) => {
      // A tab's snapshot can be text an older build copied out of a record: never a raw target (security slice).
      const snapshot = safeTabConnectionInfo(tab.connectionInfo)?.connectionName;
      if (snapshot) {
        connectionNames.add(snapshot);
      }
    });
  }

  if (connectionNames.size === 0) {
    return null;
  }

  if (connectionNames.size === 1) {
    return Array.from(connectionNames)[0];
  }

  // Multiple connections - combine first two
  const names = Array.from(connectionNames);
  if (names.length === 2) {
    return `${names[0]} & ${names[1]}`;
  }

  return `${names[0]} & ${names.length - 1} more`;
}

// ============================================================================
// Session Store
// ============================================================================

type SessionState = {
  // State
  currentSession: Session | null;
  /** All saved sessions (Pro mode) - includes current session */
  savedSessions: Session[];
  /** True if session has been loaded/initialized */
  isInitialized: boolean;
  /**
   * Connection ids the user EXPLICITLY disconnected since the last save.
   * Runtime-only — `partialize` persists `currentSession`/`savedSessions` only,
   * so an intent that never reached a save is simply forgotten (conservative:
   * the session keeps the connection rather than losing it).
   *
   * Written only by `removeConnectionFromSession`, cleared by every flow that
   * writes or replaces the session.
   */
  explicitlyDisconnectedIds: string[];
  /**
   * Id of the session the LIVE WORKSPACE currently represents — i.e. the
   * session whose tabs were restored into this workspace, or that adopted this
   * workspace's tabs on an earlier save. `null` when the workspace represents
   * no session yet (fresh launch before a restore, or right after a teardown).
   *
   * Runtime-only: `partialize` persists `currentSession`/`savedSessions` only,
   * so every app run starts with "the workspace represents nothing", which is
   * exactly true.
   *
   * This is the discriminator `saveCurrentSession` needs to tell "these tabs
   * were never brought back" from "the user closed these tabs" — two states
   * that look identical in the live workspace. `isSessionLiveInWorkspace`
   * answers a different question (is it live *right now*) and cannot separate
   * them, which is why this exists alongside it.
   */
  liveSessionId: string | null;

  // Actions
  /** Save current workspace state to session */
  saveCurrentSession: () => void;
  /**
   * Record an explicit user disconnect so the session stops carrying the
   * connection forward forever. MUST be called only from user-initiated
   * disconnect UI (Object Explorer / Connections dialog / Start Page card) or
   * from `App.applyResumeExclusions`, where the user switched a featured-card
   * connection chip off — also an explicit "not this one" — and NEVER from
   * teardown paths (app close, connection loss, session switch, validation
   * cleanup) — those are exactly the cases the carry-forward union exists to
   * protect.
   */
  removeConnectionFromSession: (connectionId: string) => void;
  /**
   * Install a decision-driven rewrite of the current session (failed-connection
   * decisions, featured-card connection exclusions) and mirror it into
   * `savedSessions` so the listed copy never lags behind the live one.
   * Deliberately NOT a save: it does not touch `explicitlyDisconnectedIds` and
   * never lists a session that wasn't listed before.
   */
  replaceCurrentSession: (session: Session) => void;
  /** Load session and restore workspace state */
  loadSession: (opts?: SessionRestoreOptions) => Promise<boolean>;
  /**
   * INTERNAL — the restore itself, without the flush/pause bookkeeping.
   * Always call `loadSession`; this exists only so that bookkeeping can wrap
   * the restore in a `try/finally` it cannot escape.
   */
  restoreSessionIntoWorkspace: (opts?: SessionRestoreOptions) => Promise<boolean>;
  /** Clear current session */
  clearSession: () => void;
  /** Check if there is a saved session */
  hasSavedSession: () => boolean;
  /** Get unique connection IDs from session */
  getSessionConnectionIds: () => string[];
  /** Update a connection's status in the current session */
  updateConnectionStatus: (connectionId: string, status: SessionConnection['status']) => void;

  // Multi-session actions (Pro mode)
  /** Get all saved sessions sorted by lastOpenedAt */
  getSavedSessions: () => Session[];
  /** Switch to a different session (saves current first) */
  switchToSession: (sessionId: string) => Promise<boolean>;
  /** Prepare to switch to a different session without loading (for validation dialog flow) */
  prepareSessionSwitch: (sessionId: string) => Promise<boolean>;
  /**
   * Close the auto-save pause a successful `prepareSessionSwitch` opened.
   *
   * `loadSession` does this itself, so callers only need it when the switch
   * ends WITHOUT a load — the user cancelling the validation dialog, or an
   * error between preparing and loading. No-op when no window is open.
   */
  endSessionSwitchWindow: () => void;
  /** Create a new session (saves current, creates fresh) */
  createNewSession: () => void;
  /** Delete a session by ID */
  deleteSession: (sessionId: string) => void;
  /** Delete all sessions (including current) */
  deleteAllSessions: () => void;
  /** Delete sessions older than specified days (excludes current session) */
  deleteSessionsOlderThan: (days: number) => number;
  /** Rename a session */
  renameSession: (sessionId: string, name: string) => void;
};

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSession: null,
      savedSessions: [],
      isInitialized: false,
      explicitlyDisconnectedIds: [],
      liveSessionId: null,

      // Actions
      saveCurrentSession: () => {
        const workspaceState = useWorkspaceStore.getState();
        const connectionState = useConnectionStore.getState();
        const { tabs, activeTabId } = workspaceState;
        const { activeConnections } = connectionState;

        // Never overwrite a session's restorable content with the nothing of a
        // workspace that never restored it.
        //
        // Dismissing the startup dialog runs `ensureStartPage()`, so 500ms
        // later the auto-save serialized a lone Start Page over the session's
        // tabs — the user's tabs were gone before they had done anything at
        // all, and "Resume Last Session" then brought back connections only.
        // The discriminator is `liveSessionId`, not "does the workspace look
        // empty": a user who restores a session and then closes every tab owns
        // that choice and must NOT have those tabs resurrected.
        const sessionBeforeSave = get().currentSession;
        const workspaceRepresentsSession =
          !!sessionBeforeSave && get().liveSessionId === sessionBeforeSave.id;
        const workspaceHasMeaningfulTabs = tabs.some((tab) => tab.type !== 'start-page');
        const sessionHasRestorableTabs =
          sessionBeforeSave?.tabs.some((tab) => tab.type !== 'start-page') ?? false;
        // Only ever preserves; the moment the workspace holds real work the
        // save tracks it as usual (one-current-session model intact).
        const preserveUnrestoredTabs =
          !workspaceRepresentsSession && !workspaceHasMeaningfulTabs && sessionHasRestorableTabs;

        // If no tabs and no active connections, don't save a session — unless
        // that would drop a session the workspace never restored (same rule as
        // above, applied to the whole record instead of just its tabs).
        if (tabs.length === 0 && activeConnections.length === 0) {
          const sessionHasSubstance =
            sessionHasRestorableTabs || (sessionBeforeSave?.connections.length ?? 0) > 0;
          if (!workspaceRepresentsSession && sessionHasSubstance) {
            return;
          }
          set({ currentSession: null, explicitlyDisconnectedIds: [], liveSessionId: null });
          return;
        }

        // Serialize tabs
        const serializedTabs = tabs.map(serializeTab);

        // Get unique connection IDs from tabs and active connections. Compare
        // and blueprint tabs keep their endpoints in `content`, so this asks
        // `tabConnectionIds` rather than reading `connectionInfo` directly.
        const neededByTabs = new Set<string>();
        tabs.forEach((tab) => {
          tabConnectionIds(tab).forEach((id) => neededByTabs.add(id));
        });

        const connectionIds = new Set<string>(neededByTabs);
        activeConnections.forEach((conn) => {
          connectionIds.add(conn.id);
        });

        // Also keep connections the session already declared, even if they are
        // no longer live in `activeConnections` (dropped, or torn down at app
        // close). A session whose only substance is its connections (e.g. just
        // the Home tab, no query/table tabs) would otherwise be rebuilt as
        // "empty" and purged from savedSessions below — silently lost on the
        // next launch. Explicit reset flows (Start Fresh / new session / switch)
        // install a fresh `currentSession`, so nothing stale is carried there.
        //
        // The carry-forward is SCOPED, though: a connection the user explicitly
        // disconnected (and that no tab still needs) is dropped, so the session
        // reflects what the user actually has rather than everything it ever
        // saw. Teardown paths never set that flag, so they still carry forward.
        const existingSession = sessionBeforeSave;
        const explicitlyDisconnected = new Set(get().explicitlyDisconnectedIds);
        existingSession?.connections.forEach((conn) => {
          const stillNeeded =
            neededByTabs.has(conn.connectionId) ||
            !explicitlyDisconnected.has(conn.connectionId);
          if (stillNeeded) {
            connectionIds.add(conn.connectionId);
          }
        });

        // Create connection entries, preserving existing status for known connections
        // This is important to preserve 'disconnected' status set during session load
        const existingConnectionMap = new Map<string, SessionConnection>(
          existingSession?.connections.map(c => [c.connectionId, c]) || []
        );

        const connections: SessionConnection[] = Array.from(connectionIds).map(
          (connectionId) => {
            const existing = existingConnectionMap.get(connectionId);
            if (existing) {
              // Preserve existing status (especially 'disconnected')
              return existing;
            }
            // New connection, status will be validated on load
            return {
              connectionId,
              status: 'unknown' as const,
            };
          }
        );

        // Create or update session
        const now = new Date().toISOString();
        const createdAt = existingSession?.createdAt || now;

        // Never regress a real name to the generic fallback: if derivation
        // comes up empty (e.g. the active connections that seeded the name
        // have since dropped out of `activeConnections`) but the session
        // still carries connections forward, keep whatever name it already
        // had instead of silently downgrading it to "Session — <date>". Only
        // a session with no previous name at all (first save, or truly no
        // connections) falls back to the dated placeholder, and that date
        // comes from the session's own `createdAt` — not `Date.now()` — so a
        // session created Monday keeps saying Monday, not "today", forever.
        // Saved config first (it holds the CURRENT alias, so a rename shows up
        // in the session name), then the live entry for anything unsaved.
        const savedConnections = useConnectionStore.getState().connections;
        const resolveConnectionName = (connectionId: string): string | undefined => {
          const saved = savedConnections.find((c) => c.id === connectionId);
          if (saved) return safeConnectionName(saved);
          const active = activeConnections.find((c) => c.id === connectionId);
          return active ? safeConnectionName(active.connection) : undefined;
        };
        const derivedName = generateSessionName(tabs, connections, resolveConnectionName);
        const name = existingSession?.customName
          ? existingSession.name
          : safeTabTitle(derivedName ??
            (connections.length > 0 && existingSession?.name
              ? existingSession.name
              : `Session — ${formatShortDate(createdAt)}`));

        const session: Session = {
          id: existingSession?.id || generateSessionId(),
          name,
          customName: existingSession?.customName,
          connections,
          // `activeTabId` travels with the tabs: pointing a preserved tab set
          // at the Start Page the user is looking at would restore the session
          // focused on a tab that isn't in it.
          tabs: preserveUnrestoredTabs ? existingSession!.tabs : serializedTabs,
          activeTabId: preserveUnrestoredTabs ? existingSession!.activeTabId : activeTabId,
          createdAt,
          lastOpenedAt: now,
        };

        // Update savedSessions array — but only list sessions with real content.
        // A session with no connections and only the default Home tab is "empty"
        // and would surface as an "Untitled Session" stub in the dialog. We still
        // keep `currentSession` populated so start-page customizations (expanded
        // sections, selected connection, etc.) persist across launches.
        const { savedSessions } = get();
        const existingIndex = savedSessions.findIndex(s => s.id === session.id);
        let updatedSessions: Session[];
        if (isEmptySession(session)) {
          updatedSessions = existingIndex >= 0
            ? savedSessions.filter((_, i) => i !== existingIndex)
            : savedSessions;
        } else {
          updatedSessions = existingIndex >= 0
            ? savedSessions.map((s, i) => i === existingIndex ? session : s)
            : [...savedSessions, session];
        }

        // The disconnect intents have now been applied to the persisted shape —
        // "since last save" restarts from here.
        //
        // A save that adopted the workspace's own tabs makes the session and
        // the workspace one and the same from here on: later saves track it
        // literally, including back down to nothing when the user closes
        // everything. A preserved save adopts nothing, so it must NOT claim
        // that congruence.
        set({
          currentSession: session,
          savedSessions: updatedSessions,
          explicitlyDisconnectedIds: [],
          ...(workspaceHasMeaningfulTabs ? { liveSessionId: session.id } : {}),
        });
      },

      removeConnectionFromSession: (connectionId: string) => {
        // The user let go of this connection on purpose. A DEFERRED connection
        // can be disconnected without its tabs closing (Start Page / Connections
        // dialog), so unless lazy connect forgets it here, the next click on one
        // of those tabs would quietly reopen it — the exact thing lazy connect
        // promises never to do.
        forgetLazyConnect(connectionId);

        const { currentSession, savedSessions, explicitlyDisconnectedIds } = get();

        // Remember the intent even when there is nothing to prune right now —
        // `saveCurrentSession` re-applies the rule against the live workspace.
        const nextDisconnectedIds = explicitlyDisconnectedIds.includes(connectionId)
          ? explicitlyDisconnectedIds
          : [...explicitlyDisconnectedIds, connectionId];

        // A tab still pointing at this connection keeps it declared: the tab
        // needs the connection back to be usable, so the user still "has" it.
        // Compare tabs point at two connections and blueprint tabs at their
        // active link, none of which live in `connectionInfo` — hence the
        // helper rather than a direct read.
        const tabReferenced = useWorkspaceStore
          .getState()
          .tabs.some((tab) => tabConnectionIds(tab).includes(connectionId));

        const declared = currentSession?.connections.some(
          (conn) => conn.connectionId === connectionId
        );

        if (!currentSession || tabReferenced || !declared) {
          set({ explicitlyDisconnectedIds: nextDisconnectedIds });
          return;
        }

        // Prune eagerly so `session.connections` is honest immediately (the
        // Sessions dialog and any resume flow read it directly). This is NOT
        // just cosmetic: `disconnectFromDatabase` de-duplicates in-flight
        // requests, so a caller can join a disconnect already in progress and
        // resolve only AFTER the autosave that disconnect triggered has
        // committed. With no further save scheduled, the flag alone would leave
        // the persisted shape stale until some unrelated workspace change; this
        // write corrects it synchronously.
        const updatedSession: Session = {
          ...currentSession,
          connections: currentSession.connections.filter(
            (conn) => conn.connectionId !== connectionId
          ),
        };

        // Mirror into savedSessions, honouring the same "empty sessions are not
        // listable" invariant `saveCurrentSession` maintains.
        const updatedSavedSessions = isEmptySession(updatedSession)
          ? savedSessions.filter((s) => s.id !== updatedSession.id)
          : savedSessions.map((s) => (s.id === updatedSession.id ? updatedSession : s));

        set({
          currentSession: updatedSession,
          savedSessions: updatedSavedSessions,
          explicitlyDisconnectedIds: nextDisconnectedIds,
        });
      },

      replaceCurrentSession: (session: Session) => {
        const { savedSessions } = get();

        // Same "empty sessions are not listable" invariant `saveCurrentSession`
        // maintains — an exclusion that empties a connection-only session must
        // not leave a stub behind in the list.
        const updatedSavedSessions = isEmptySession(session)
          ? savedSessions.filter((s) => s.id !== session.id)
          : savedSessions.map((s) => (s.id === session.id ? session : s));

        set({ currentSession: session, savedSessions: updatedSavedSessions });
      },

      loadSession: async (opts?: SessionRestoreOptions) => {
        // Flush before restore. `restoreTabs` REPLACES the tab set, so a tab the
        // user opened in the last 500ms — still sitting in the debounce, not yet
        // in any session — would be wiped with no trace. Committing the queued
        // save first means the session model reflects reality at teardown entry,
        // and the restore hands that work straight back instead of dropping it.
        // Only ever commits real work: a queued save against an already-empty
        // workspace is dropped, never written over the tabs we are about to
        // restore. No-op when nothing is queued — every switch (already paused)
        // and every cold start.
        flushPendingSessionAutoSaveBeforeRestore();

        try {
          return await get().restoreSessionIntoWorkspace(opts);
        } finally {
          // Terminal step of a switch: the workspace is whole again, so the
          // pause `prepareSessionSwitch` opened has to come off — including
          // when the restore threw.
          releaseSessionSwitchPause();
        }
      },

      endSessionSwitchWindow: () => {
        releaseSessionSwitchPause();
      },

      restoreSessionIntoWorkspace: async (opts?: SessionRestoreOptions) => {
        const { currentSession } = get();
        set({ isInitialized: true });

        // Always set the tab history session ID if we have a current session
        // (even if the session has no content yet)
        if (currentSession?.id) {
          useTabHistoryStore.getState().setCurrentSession(currentSession.id);
        }

        if (!currentSession || (currentSession.tabs.length === 0 && currentSession.connections.length === 0)) {
          if (!isUnitTest()) console.log('[SessionStore] No session, tabs, or connections to restore');
          return false;
        }

        // Baseline for the emitted durationMs — restore wall-clock (reconnects
        // + tab deserialization + store commit). The post-restore freeze-feel
        // (Monaco mounts, introspection) is measured separately by
        // app:startup_ready; this value scopes the restore flow itself.
        const restoreStartedAt = Date.now();

        const connectionStore = useConnectionStore.getState();
        const workspaceStore = useWorkspaceStore.getState();

        // Get saved connections
        const savedConnections = connectionStore.connections;
        if (!isUnitTest()) console.log('[SessionStore] Saved connections available:', savedConnections.map(c => ({
          id: c.id,
          name: safeConnectionName(c),
          engine: c.engine || 'mssql',
        })));

        // Collect unique connection IDs from session connections AND tabs
        const connectionIds = new Set<string>();

        // First, add all connections from the session (includes connections without tabs)
        currentSession.connections.forEach((conn) => {
          connectionIds.add(conn.connectionId);
        });

        // Also add any connections referenced by tabs (in case session.connections is
        // incomplete). Sessions written before the save learned to declare compare and
        // blueprint endpoints are exactly that case, and their endpoints survive only
        // in tab content — so this reads the tab's real need, not `connectionInfo`.
        currentSession.tabs.forEach((tab) => {
          tabConnectionIds(tab).forEach((id) => connectionIds.add(id));
        });
        if (!isUnitTest()) console.log('[SessionStore] Connection IDs to restore:', Array.from(connectionIds));

        // Collect connections to restore (including previously disconnected ones - they might be back online)
        const connectionsToRestore: Array<{ id: string; connection: Connection }> = [];
        for (const connectionId of connectionIds) {
          const connection = savedConnections.find((c) => c.id === connectionId);
          if (connection) {
            connectionsToRestore.push({ id: connectionId, connection });
          } else {
            if (!isUnitTest()) console.warn(`[SessionStore] Connection config not found for ${connectionId}`);
          }
        }

        if (!isUnitTest()) console.log('[SessionStore] Connections to restore:',
          connectionsToRestore.map((c) => ({ name: safeConnectionName(c.connection), engine: c.connection.engine || 'mssql' }))
        );

        // Seven outcomes so telemetry can tell real reconnect failures apart
        // from deliberate non-attempts — a boolean here made every Entra user
        // look like a restore failure on the release-health dashboard.
        // 'restored' = one of THIS restore's eager connects succeeded;
        // 'already-connected' = nothing to do, it was open before the restore
        // began (kept separate so `connectionsRestored` stays a clean
        // "the eager connects worked" signal instead of also counting these);
        // 'skipped' = attemptable but deliberately not attempted (Entra needs
        // interactive auth); 'deferred' = handed to the background sweep, which
        // opens it right after the workspace comes back (its OUTCOME lands on
        // `feature:session_restore_background`, not here);
        // 'awaiting-password' = left for first use because opening it raises the
        // single-instance password prompt; 'kept-offline' = the user answered
        // "Keep offline" in the validation dialog, so it is never opened (kept
        // OUT of 'skipped' so the Entra signal stays readable); 'missing-config'
        // = the session references a connection whose saved config no longer
        // exists (deleted connection — or, if it ever spikes, a
        // connections-persistence regression).
        const connectionResults = new Map<
          string,
          | 'restored'
          | 'already-connected'
          | 'failed'
          | 'skipped'
          | 'deferred'
          | 'awaiting-password'
          | 'kept-offline'
          | 'missing-config'
        >();

        // Helper to reconnect a connection
        const reconnect = async (id: string, connection: Connection): Promise<boolean> => {
          if (!isUnitTest()) console.log(`[SessionStore] Attempting to reconnect ${id}:`, {
            name: safeConnectionName(connection),
            engine: connection.engine || 'mssql',
            server: safeConnectionServer(connection),
          });
          try {
            // Session-restore reconnect — never a first-connect bridge trigger.
            const success = await connectionStore.connectToDatabase(connection, { initiator: 'restore' });
            if (!isUnitTest()) console.log(`[SessionStore] Reconnect result for ${id} (${safeConnectionName(connection)}):`, success);
            return success;
          } catch (error) {
            if (!isUnitTest()) console.error(`[SessionStore] Failed to reconnect to ${id}:`, error);
            return false;
          }
        };

        // Seed a connection the resume is NOT opening as a `disconnected` entry
        // in `activeConnections` (the shape the Entra skip has always produced).
        // It stays visible in the Object Explorer with its reconnect button, and
        // it is exactly the state the tab-strip dot and the workspace connection
        // banner read as "this tab's connection isn't open"
        // (Docs/ConnectionBanner.md). Reads fresh state on every call so seeding
        // several in a row can't clobber earlier ones.
        const seedDisconnected = (id: string, connection: Connection, error?: string) => {
          const store = useConnectionStore.getState();
          // Never contradict a connection that is open, or one that is being
          // opened right now. `alreadyConnectedIds` is a snapshot taken at the
          // top of the restore, and the resume dialog now dials in the
          // background — so a connect can land between that snapshot and this
          // call. Seeding it `disconnected` then marks a live pool dead, which
          // is the exact failure this whole round is about.
          if (
            store.activeConnections.find((c) => c.id === id)?.status === 'connected' ||
            store.connectionOperationById[id] === 'connecting'
          ) {
            return;
          }
          const present = useConnectionStore
            .getState()
            .activeConnections.some((c) => c.id === id);
          if (present) {
            useConnectionStore.getState().markConnectionDisconnected(id);
            // `markConnectionDisconnected` only flips the status, so a reason
            // would be lost on an entry that already existed — and the reason
            // is the whole point of passing one.
            if (error) {
              useConnectionStore.setState((state) => ({
                activeConnections: state.activeConnections.map((conn) =>
                  conn.id === id ? { ...conn, error } : conn
                ),
              }));
            }
            return;
          }
          useConnectionStore.setState((state) => ({
            activeConnections: [
              ...state.activeConnections,
              {
                id,
                connection,
                status: 'disconnected' as const,
                ...(error ? { error } : {}),
              },
            ],
          }));
        };

        // CONNECT ON RESUME — the resume opens the connections the restored
        // workspace lands on BEFORE handing the workspace back: one for an
        // ordinary active tab, both endpoints for a Schema/Data Compare tab, the
        // linked connection for a Database Blueprint tab. Everything else is
        // seeded closed and opened by the background sweep the moment the
        // restore commits (`sweepPendingLazyConnects`, below) — so a session
        // that has a connection gets that connection back, while a server that
        // is down or VPN-gated still costs the workspace nothing: it is already
        // on screen when the dial fails. `savedConnections` makes the resolution skip
        // candidates that cannot be auto-connected (deleted config, Entra)
        // instead of spending an eager slot on them — and it is the same call
        // the featured card's chips and the validation probe make, so all three
        // name the same connections.
        //
        // "Keep offline" (E7) is honoured here too: a connection the user chose
        // to keep offline is dropped from the candidate list BEFORE the eager
        // slot is resolved, so the failed connection the user just declined
        // cannot win it back. The eager slot then falls through to the next
        // viable candidate — the same thing that happens for a deleted config
        // or an Entra connection.
        const keptOfflineIds = new Set(opts?.keptOfflineIds ?? []);
        const eagerConnectionIds = new Set(
          resolveEagerConnectionIds(
            currentSession,
            keptOfflineIds.size > 0
              ? savedConnections.filter((c) => !keptOfflineIds.has(c.id))
              : savedConnections
          )
        );
        // Presence in `activeConnections` is not connectedness — status-filter
        // through the one helper that knows that (sessionDisplayUtils).
        const alreadyConnectedIds = new Set(
          getConnectedConnectionIds(useConnectionStore.getState().activeConnections)
        );
        const deferredIds: string[] = [];
        const eagerTargets: Array<{ id: string; connection: Connection }> = [];

        for (const { id, connection } of connectionsToRestore) {
          if (alreadyConnectedIds.has(id)) {
            connectionResults.set(id, 'already-connected');
            continue;
          }

          if (keptOfflineIds.has(id)) {
            // The user was shown "Will be kept offline - queries will be
            // viewable but not executable" and said yes. Deliberately NOT
            // deferred, exactly like the Entra skip below: connect-on-demand
            // would break the same promise on the first tab click.
            seedDisconnected(id, connection);
            connectionResults.set(id, 'kept-offline');
            continue;
          }

          if (isEntraInteractiveConnection(connection)) {
            if (!isUnitTest()) {
              console.log(
                `[SessionStore] Skipping auto-reconnect for Entra connection ${id} (${safeConnectionName(connection)})`
              );
            }
            // Deliberately NOT deferred: connect-on-demand would pop an
            // interactive auth window from a plain tab click.
            seedDisconnected(id, connection, 'Entra ID connections require manual authentication');
            connectionResults.set(id, 'skipped');
            continue;
          }

          if (eagerConnectionIds.has(id)) {
            eagerTargets.push({ id, connection });
            continue;
          }

          seedDisconnected(id, connection);
          deferredIds.push(id);
          // Both populations go into lazy connect's pending set — the
          // difference is only who opens them. The background sweep below takes
          // everything except the password-prompt ones, which keep the original
          // on-first-use contract because the prompt is single-instance
          // (`connectPasswordPromptStore`: a second request cancels the first).
          connectionResults.set(
            id,
            connectionNeedsPasswordPrompt(connection) ? 'awaiting-password' : 'deferred'
          );
        }

        // Replaces the previous restore's deferrals — never accumulates.
        markConnectionsLazy(deferredIds);

        // Restore tabs (even for failed connections - they'll show as disconnected)
        const restoredTabs = currentSession.tabs.map(deserializeTab);

        // Restore tabs BEFORE the eager connect: the workspace is the thing the
        // user is waiting for, and every data-loading surface is already gated
        // on its connection being live (and re-fires when it lands), so nothing
        // races. `liveSessionId` is written in the same breath — a workspace
        // that holds a session's tabs but isn't marked as representing it is
        // the exact state E6's guard mistakes for "the user closed these tabs".
        workspaceStore.restoreTabs(restoredTabs, currentSession.activeTabId);

        // Note: Explorer expanded state is restored in DatabaseExplorer component
        // after the tree is built, allowing synchronous data loading.

        set({
          currentSession: {
            ...currentSession,
            lastOpenedAt: new Date().toISOString(),
          },
          // The workspace now IS this session, so later saves track it
          // literally — including down to nothing if the user closes every tab.
          liveSessionId: currentSession.id,
        });

        // The eager connects — normally one, two when the restored active tab
        // is a Schema/Data Compare (it needs both endpoints to be usable).
        // Sequential on purpose: the tabs are already back, so nothing is
        // waiting on this, and one connect at a time keeps the ordering (and
        // any password prompt) predictable. MSSQL and PostgreSQL both use
        // driver-based connections; language servers (SQL Tools Service for
        // MSSQL, pg-language-server for PostgreSQL) initialize in the
        // background for IntelliSense only.
        // Which session these results describe. Each connect is awaited, and a
        // session switch during that await makes everything computed here about
        // a session the workspace no longer holds.
        const restoreForSessionId = currentSession.id;

        for (const eagerTarget of eagerTargets) {
          if (get().currentSession?.id !== restoreForSessionId) break;
          const success = await reconnect(eagerTarget.id, eagerTarget.connection);
          if (!success) {
            // A failed eager connect used to record 'failed' and nothing else,
            // so it rendered as a bare amber "Disconnected" — indistinguishable
            // from a connection the resume deliberately never attempted, with
            // no reason offered for either. `connectToDatabase` DOES store the
            // reason; carry it onto the seeded entry, exactly as the Entra skip
            // carries its own.
            seedDisconnected(
              eagerTarget.id,
              eagerTarget.connection,
              useConnectionStore.getState().connectionErrorById[eagerTarget.id]
            );
          }
          connectionResults.set(eagerTarget.id, success ? 'restored' : 'failed');
        }

        // Connections with no saved config were never attempted — count every
        // one, including those the session already marked disconnected (the
        // status mapping below treats absent-from-map and anything other
        // than 'restored'/'already-connected' identically, so counting them
        // can't change connected statuses, and leaving them uncounted made
        // the emitted totals miss connections that deliberately weren't
        // attempted).
        for (const connectionId of connectionIds) {
          if (!connectionResults.has(connectionId)) {
            connectionResults.set(connectionId, 'missing-config');
          }
        }

        if (!isUnitTest()) console.log('[SessionStore] Final connection results:', Object.fromEntries(connectionResults));

        // Update connection statuses in session based on actual outcomes. Read
        // through the store rather than the captured `currentSession`: the
        // restore awaited a connect, and the session may have been rewritten
        // meanwhile.
        set((state) => {
          if (!state.currentSession) return {};
          // ...but only if it is still the SAME session. Reading through the
          // store protects against the record being replaced; it does not
          // protect against it being replaced by a DIFFERENT session, which is
          // what a switch mid-restore does. Writing session A's outcomes onto
          // session B marks B's live connections 'disconnected', and the next
          // save persists that lie.
          if (state.currentSession.id !== restoreForSessionId) return {};
          const updatedConnections: SessionConnection[] = state.currentSession.connections.map(
            (conn) => ({
              ...conn,
              status:
                connectionResults.get(conn.connectionId) === 'restored' ||
                connectionResults.get(conn.connectionId) === 'already-connected'
                  ? 'connected'
                  : 'disconnected',
            })
          );
          return {
            currentSession: { ...state.currentSession, connections: updatedConnections },
          };
        });

        // BACKGROUND SWEEP — the restore is committed (tabs are in the
        // workspace, `liveSessionId` is set, statuses are written), so from here
        // on nothing the user is waiting for depends on a connection opening.
        // Deliberately NOT awaited: awaiting it would put every saved
        // connection's dial back in front of the workspace, which is the
        // blocking resume this split exists to remove. `requestLazyConnect`
        // carries the guards (at-most-once, in-flight join, session epoch,
        // intent epoch), so a session switch or a hand disconnect landing
        // mid-sweep is already handled.
        void sweepPendingLazyConnects().then((sweep) => {
          if (sweep.attempted === 0) return;
          // Its own event, NOT `connectionsFailed` on the restore event: the
          // release-health dashboard alarms on session-restore failures at
          // > 0.5%, and a background dial of every saved connection means every
          // unreachable/VPN-gated server a user ever saved would hold that
          // alarm permanently red. This event is where "the sweep couldn't
          // reach N servers" is legible — and its `durationMs` is background
          // time, deliberately not folded into the restore's time-to-workspace.
          if (typeof window === 'undefined') return;
          window.electronAPI?.analytics
            ?.track('feature:session_restore_background', {
              connectionsAttempted: sweep.attempted,
              connectionsConnected: sweep.connected,
              connectionsFailed: sweep.failed,
              durationMs: Date.now() - restoreStartedAt,
            })
            .catch(() => {});
        });

        // Release-observability: outcome telemetry for session restore. Low
        // cardinality only — counts, never connection ids/aliases/servers.
        // `connectionsFailed` counts ONLY attempted reconnects that failed;
        // deliberate non-attempts split into `connectionsSkipped` (Entra
        // interactive auth), `connectionsKeptOffline` (the user answered "Keep
        // offline" in the validation dialog) and `connectionsMissingConfig`
        // (session references a deleted/absent saved connection). The
        // release-health dashboard's
        // restore metric alarms on connectionsFailed > 0, so folding either
        // into it would make every Entra user a standing false alarm — but a
        // SPIKE in the other two is its own signal (a connections-persistence
        // regression would empty savedConnections and go quiet-green here).
        //
        // `connectionsDeferred` is the population handed to the BACKGROUND
        // SWEEP: attemptable, not attempted before the workspace came back,
        // dialed moments later off the critical path. Its outcomes land on
        // `feature:session_restore_background`, not here — this event is
        // emitted the instant the workspace is usable, which is before the
        // sweep can have finished, and holding it back would poison
        // `durationMs` (time-to-workspace) with background connect time.
        // `connectionsAwaitingPassword` is the one population that genuinely
        // still waits for first use: the on-connect password prompt is
        // single-instance, so those cannot be opened in parallel behind the
        // user's back. Split from `connectionsDeferred` because "the sweep will
        // get it" and "nothing will get it until you click" are different
        // promises, and only the second one can strand a connection.
        // `connectionsRestored` counts ONLY this restore's eager connects —
        // one per connection the restored ACTIVE TAB needs, so 0..N and in
        // practice 0–2 (a Schema/Data Compare active tab needs both endpoints).
        // Connections that were already open before the restore began are
        // counted as `connectionsAlreadyConnected` instead (the
        // resume-an-already-live-session path can emit N of those, and folding
        // them in would make "the eager connects worked" unreadable).
        let connectionsRestored = 0;
        let connectionsAlreadyConnected = 0;
        let connectionsFailed = 0;
        let connectionsSkipped = 0;
        let connectionsDeferred = 0;
        let connectionsAwaitingPassword = 0;
        let connectionsKeptOffline = 0;
        let connectionsMissingConfig = 0;
        for (const outcome of connectionResults.values()) {
          if (outcome === 'restored') connectionsRestored += 1;
          else if (outcome === 'already-connected') connectionsAlreadyConnected += 1;
          else if (outcome === 'failed') connectionsFailed += 1;
          else if (outcome === 'missing-config') connectionsMissingConfig += 1;
          else if (outcome === 'deferred') connectionsDeferred += 1;
          else if (outcome === 'awaiting-password') connectionsAwaitingPassword += 1;
          else if (outcome === 'kept-offline') connectionsKeptOffline += 1;
          else connectionsSkipped += 1;
        }
        if (typeof window !== 'undefined') {
          window.electronAPI?.analytics
            ?.track('feature:session_restore', {
              connectionsRestored,
              connectionsAlreadyConnected,
              connectionsFailed,
              connectionsSkipped,
              connectionsDeferred,
              connectionsAwaitingPassword,
              connectionsKeptOffline,
              connectionsMissingConfig,
              tabs: restoredTabs.length,
              // queryTabs lets the dashboard normalize duration (a 20-editor
              // restore is legitimately slower than a 2-tab one).
              queryTabs: restoredTabs.filter((t) => t.type === 'query').length,
              durationMs: Date.now() - restoreStartedAt,
            })
            .catch(() => {});
        }

        return true;
      },

      clearSession: () => {
        // Disconnect intents belong to the session they were recorded against.
        set({ currentSession: null, explicitlyDisconnectedIds: [], liveSessionId: null });
      },

      hasSavedSession: () => {
        const { currentSession } = get();
        if (!currentSession) return false;
        return !isEmptySession(currentSession);
      },

      getSessionConnectionIds: () => {
        const { currentSession } = get();
        if (!currentSession) return [];
        return currentSession.connections.map((c) => c.connectionId);
      },

      updateConnectionStatus: (connectionId, status) => {
        const { currentSession } = get();
        if (!currentSession) return;

        // Check if connection exists in session
        const existingConn = currentSession.connections.find(
          (conn) => conn.connectionId === connectionId
        );

        let updatedConnections;
        if (existingConn) {
          // Update existing connection
          updatedConnections = currentSession.connections.map((conn) =>
            conn.connectionId === connectionId ? { ...conn, status } : conn
          );
        } else {
          // Add new connection with the given status
          updatedConnections = [
            ...currentSession.connections,
            { connectionId, status },
          ];
        }

        set({
          currentSession: {
            ...currentSession,
            connections: updatedConnections,
          },
        });
      },

      // Multi-session actions (Pro mode)
      getSavedSessions: () => {
        const { savedSessions } = get();
        // Sort by lastOpenedAt descending (most recent first)
        return [...savedSessions].sort(
          (a, b) => new Date(b.lastOpenedAt).getTime() - new Date(a.lastOpenedAt).getTime()
        );
      },

      switchToSession: async (sessionId: string) => {
        const { prepareSessionSwitch, loadSession } = get();

        // Prepare the switch (save current, disconnect, set target as current)
        const prepared = await prepareSessionSwitch(sessionId);
        if (!prepared) {
          return false;
        }

        // Load the session (will restore tabs and reconnect)
        return loadSession();
      },

      prepareSessionSwitch: async (sessionId: string) => {
        // Pause FIRST — before anything reads or writes a store. Everything
        // below (the explicit save, each disconnect, the tab wipe) is a change
        // the auto-save subscription reacts to, and the window stays open long
        // after this function resolves: the incoming session is only restored
        // once validation finishes, which can wait on the user. A debounce
        // landing anywhere in there would serialize a half-torn-down workspace
        // over the session — the reported rename-to-Untitled / emptied-session
        // bug. Pausing also cancels the debounce already queued by whatever the
        // user did just before switching; the explicit `saveCurrentSession()`
        // below commits that work properly instead.
        pauseAutoSaveForSessionSwitch();

        // Only the teardown path keeps the window open (until load, or an
        // explicit `endSessionSwitchWindow` on cancel). Every other exit —
        // early return, thrown disconnect — must give auto-save back, or it
        // stays off for the rest of the app run.
        let switchIsUnderway = false;
        try {
          const { currentSession, savedSessions, saveCurrentSession } = get();
          const workspaceStore = useWorkspaceStore.getState();
          const connectionStore = useConnectionStore.getState();

          // Find target session
          const targetSession = savedSessions.find(s => s.id === sessionId);
          if (!targetSession) {
            if (!isUnitTest()) console.warn('[SessionStore] Session not found:', sessionId);
            return false;
          }

          // If already on this session, nothing to prepare — and nothing to
          // tear down either, so the live session's deferrals stay valid.
          if (currentSession?.id === sessionId) {
            return true;
          }

          // The outgoing session's deferred connections belong to it alone: a
          // pending lazy id surviving the switch would let a tab click in the
          // INCOMING session open a connection the incoming session never
          // declared (and graft it onto that session's record).
          clearLazyConnectState();

          // Save current session before switching
          if (currentSession) {
            saveCurrentSession();
          }

          // Disconnect all current connections
          for (const conn of connectionStore.activeConnections) {
            await connectionStore.disconnectFromDatabase(conn.id);
          }

          // The loop above only reaches connections that have already landed.
          // One still dialing is owned by nobody, so disconnecting it is a
          // silent no-op — it would land afterwards and be adopted into the
          // INCOMING session. Withdraw the outgoing session's intent for every
          // id it declared, whether or not it got as far as connecting.
          for (const conn of currentSession?.connections ?? []) {
            connectionStore.abandonConnect(conn.connectionId);
          }

          // Close all tabs using restoreTabs action
          workspaceStore.restoreTabs([], null);

          // Set target as current session. The teardown disconnects above are NOT
          // user intent, and any intent recorded against the outgoing session was
          // already applied by `saveCurrentSession` — start the target clean.
          // The workspace now represents no session at all, so `liveSessionId`
          // goes with it: the target's tabs have not been restored yet.
          set({
            currentSession: targetSession,
            isInitialized: false,
            explicitlyDisconnectedIds: [],
            liveSessionId: null,
          });

          switchIsUnderway = true;
          return true;
        } finally {
          if (!switchIsUnderway) releaseSessionSwitchPause();
        }
      },

      createNewSession: () => {
        const { currentSession, saveCurrentSession } = get();
        const workspaceStore = useWorkspaceStore.getState();
        const connectionStore = useConnectionStore.getState();

        // Save current session if it has content
        if (currentSession && (currentSession.tabs.length > 0 || currentSession.connections.length > 0)) {
          saveCurrentSession();
        }

        // Get savedSessions AFTER saveCurrentSession() to get the updated list
        // (saveCurrentSession modifies savedSessions in the store)
        const { savedSessions } = get();

        // Drop empty sessions (no connections + only the default Home tab) so they
        // don't pile up as "Untitled Session" entries when users churn through new
        // sessions without adding content.
        const filteredSessions = savedSessions.filter((s) => !isEmptySession(s));

        // A fresh session inherits none of the outgoing session's deferred
        // connections — same reason as `prepareSessionSwitch`.
        clearLazyConnectState();

        // Disconnect all connections (async but don't wait)
        connectionStore.activeConnections.forEach((conn) => {
          connectionStore.disconnectFromDatabase(conn.id);
        });

        // Close all tabs using restoreTabs action
        workspaceStore.restoreTabs([], null);

        // Create a fresh session
        const newSession: Session = {
          id: generateSessionId(),
          name: 'Untitled Session',
          connections: [],
          tabs: [],
          activeTabId: null,
          createdAt: new Date().toISOString(),
          lastOpenedAt: new Date().toISOString(),
        };

        // Set as current session but DON'T add to savedSessions yet
        // The session will be added to savedSessions when it gets content (via saveCurrentSession)
        set({
          currentSession: newSession,
          savedSessions: filteredSessions,
          isInitialized: true,
          // The teardown disconnects above are not user intent, and the fresh
          // session declares no connections to prune.
          explicitlyDisconnectedIds: [],
          // The deliberately-emptied workspace IS the new session, so saves
          // track it from the start (there is nothing of its own to preserve).
          liveSessionId: newSession.id,
        });

        // Update tab history to track this new session
        useTabHistoryStore.getState().setCurrentSession(newSession.id);
      },

      deleteSession: (sessionId: string) => {
        const { currentSession, savedSessions } = get();

        // Cannot delete current active session
        if (currentSession?.id === sessionId) {
          if (!isUnitTest()) console.warn('[SessionStore] Cannot delete active session');
          return;
        }

        // Remove from savedSessions
        const updatedSessions = savedSessions.filter(s => s.id !== sessionId);
        set({ savedSessions: updatedSessions });

        // Clear tab history for the deleted session
        useTabHistoryStore.getState().clearSessionHistory(sessionId);
      },

      deleteAllSessions: () => {
        set({
          currentSession: null,
          savedSessions: [],
          isInitialized: true,
          explicitlyDisconnectedIds: [],
          liveSessionId: null,
        });
        // Clear tab history session tracking
        useTabHistoryStore.getState().setCurrentSession(null);
      },

      deleteSessionsOlderThan: (days: number) => {
        const { currentSession, savedSessions } = get();
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);

        // Find sessions to delete (older than cutoff, but never the current session)
        const sessionsToDelete = savedSessions.filter((session) => {
          if (currentSession?.id === session.id) return false;
          const lastOpened = new Date(session.lastOpenedAt);
          return lastOpened < cutoffDate;
        });

        // Remove from savedSessions
        const updatedSessions = savedSessions.filter(
          (session) => !sessionsToDelete.some((s) => s.id === session.id)
        );

        set({ savedSessions: updatedSessions });

        // Clear tab history for deleted sessions
        sessionsToDelete.forEach((session) => {
          useTabHistoryStore.getState().clearSessionHistory(session.id);
        });

        return sessionsToDelete.length;
      },

      renameSession: (sessionId: string, name: string) => {
        const { currentSession, savedSessions } = get();

        // Update in savedSessions
        const updatedSessions = savedSessions.map(s =>
          s.id === sessionId ? { ...s, name, customName: true } : s
        );

        // Also update current session if it's the one being renamed
        const updatedCurrent = currentSession?.id === sessionId
          ? { ...currentSession, name, customName: true }
          : currentSession;

        set({
          currentSession: updatedCurrent,
          savedSessions: updatedSessions,
        });

        // Plain (non-React) store action, shared by every rename surface
        // (list-card double-click, list-card context menu, both featured-card
        // mounts) — fire-and-forget, same shape as feature:session_restore
        // above. No session name or other user text in props (low-cardinality
        // only), matching the analytics PII rule.
        if (typeof window !== 'undefined') {
          window.electronAPI?.analytics?.track('feature:session_rename', {}).catch(() => {});
        }
      },
    }),
    {
      name: 'sql-studio-session',
      version: 1,
      // Never let a persist write throw into the auto-save timer (quota /
      // V8 string-length failures were uncaught renderer crashes).
      storage: createGuardedJsonStorage({ onFailure: showPersistWriteFailureToast('your workspace') }),
      migrate: (persistedState) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return persistedState;
        }

        const state = persistedState as {
          currentSession?: Record<string, unknown> | null;
          savedSessions?: Array<Record<string, unknown>>;
        };

        const stripExplorerState = (
          session: Record<string, unknown> | null | undefined
        ): Record<string, unknown> | null | undefined => {
          if (!session || typeof session !== 'object') return session;
          const result = { ...session } as { explorerState?: unknown };
          delete result.explorerState;
          return result;
        };

        return {
          ...state,
          currentSession: stripExplorerState(state.currentSession) || null,
          savedSessions: (state.savedSessions || []).map((session) => stripExplorerState(session) || session),
        };
      },
      // Oversized query bodies are stripped from the persisted snapshot only
      // (in-memory sessions keep them) — see sessionPersistenceCap.ts.
      partialize: (state) => ({
        currentSession: capSessionForPersistence(state.currentSession),
        savedSessions: capSessionsForPersistence(state.savedSessions),
      }),
      // When session store rehydrates, sync the session ID to tab history immediately
      // This ensures closed tabs are tracked even before loadSession() is called.
      // Also drops empty sessions (no connections + only Home tab) from savedSessions
      // so they don't clutter the Sessions dialog on next launch — clutter persisted
      // by earlier builds that listed every auto-created start-page state. We leave
      // `currentSession` untouched so start-page customizations still restore.
      onRehydrateStorage: () => (state) => {
        if (!state) return;

        // Security slice: an older build could persist raw target text in a session name or its tabs. It is made
        // safe first — the helpers are total, so a damaged record cannot abort the rest — and when that changed
        // anything the same fields are rewritten in the stored file once, right away, nothing else in it touched
        // (Fable review S6; review round 2: ordinary persistence would cap SQL bodies). A microtask: with synchronous
        // storage this runs while the store is still being created.
        const safeSaved = mapIfArray(keepRecords(state.savedSessions), safeSession);
        const safeCurrent = readableSession(state.currentSession);
        if (safeSaved !== state.savedSessions || safeCurrent !== state.currentSession) {
          queueMicrotask(() => rewritePersistedState('sql-studio-session', safeStoredSessions));
        }
        state.savedSessions = safeSaved;
        state.currentSession = safeCurrent;

        if (Array.isArray(state.savedSessions)) {
          const cleanedSavedSessions = state.savedSessions.filter((s) => !isEmptyStoredSession(s));
          if (cleanedSavedSessions.length !== state.savedSessions.length) {
            state.savedSessions = cleanedSavedSessions;
          }
        }

        if (state.currentSession?.id) {
          useTabHistoryStore.getState().setCurrentSession(state.currentSession.id);
        }
      },
    }
  )
);
